import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { log } from '@cccom/shared/logging';

import { useSettings } from '../use-settings';

type FormatInTimeZoneProps = Parameters<typeof formatInTimeZone>;
type DisplayDateProps = {
  date: FormatInTimeZoneProps[0];
  timeZone?: FormatInTimeZoneProps[1];
  format?: 'date' | 'time' | 'datetime';
  options?: FormatInTimeZoneProps[3];
};

type UseDateProps = {
  overrideTimezone?: string;
};

function useDate(props?: UseDateProps) {
  const [t] = useTranslation();
  const { timezone: settingsTimeZone } = useSettings();

  const timezone = props?.overrideTimezone ?? settingsTimeZone;

  const displayDate = useCallback(
    ({
      date,
      timeZone = timezone,
      format = 'datetime',
      options,
    }: DisplayDateProps) => {
      try {
        return formatInTimeZone(
          date,
          timeZone,
          t(`common.formats.${format}`),
          options
        );
      } catch (err: unknown) {
        log.error('Date Error: date could not be formatted', err);
        return t('common.errors.invalid_date');
      }
    },
    [timezone, t]
  );

  const toLocaleDateString = useCallback((date: Date) => {
    return date.toLocaleDateString('en-GB', {
      dateStyle: 'short',
    });
  }, []);

  const getTimezoneOffsetString = useCallback(() => {
    const milliseconds = fromZonedTime(new Date(), timezone);
    let timeZoneName = 'GMT+0';
    try {
      timeZoneName =
        Intl.DateTimeFormat('en', {
          timeZoneName: 'short',
          timeZone: timezone,
        })
          .formatToParts(milliseconds)
          .find((i) => i.type === 'timeZoneName')?.value ?? 'GMT+0';
    } catch (err: unknown) {
      log.error('Date Error: timezone could not be determined', err);
    }

    const symbol = timeZoneName.substring(0, 4);
    const hour = timeZoneName.substring(4);
    const offsetHour = hour.length > 1 ? `${hour}00` : `0${hour}00`;

    const offset =
      timeZoneName.length > 3 ? `${symbol}${offsetHour}` : `${timeZoneName}`;

    return `(${offset}) ${timezone}`;
  }, [timezone]);

  return {
    timezone,
    timezoneOffset: getTimezoneOffsetString(),
    displayDate,
    toLocaleDateString,
  };
}

export default useDate;
